import { useQuery } from '@hooks/reactQuery';

import { getAutomaticGranularity } from '@api/kpi';

import { extractQueryParams } from '@utils/common';
import { getDateSelectionParameters } from '@components/Kpi/Parameters/utils';

import { TNameTitle } from '@interfaces/common/common';
import { IGranularitiesOptionsDTO } from '@interfaces/kpi/dto/kpiDTO';

import { EUserRoles } from '@enums/common';
import { EDateSelectionModes, EDynamicDateRange } from '@enums/kpi-api';
import { EGranularityValue } from '@enums/kpi-common';

import { refetchOptions } from '@constants/common';
import { OPTIONS_GRANULARITIES_AUTOMATIC } from '@constants/queryKeys';

interface IUseQueryOptionsGranularitiesAutomatic {
	automaticGranularity?: TNameTitle<EGranularityValue>;
	isLoadingOptionsAutomaticGranularity: boolean;
}

interface IUseQueryOptionsGranularities extends IGranularitiesOptionsDTO {
	isOranVendor: boolean;
}

export const useQueryOptionsGranularityAutomatic = (
	props: IUseQueryOptionsGranularities,
): IUseQueryOptionsGranularitiesAutomatic => {
	const {
		isOranVendor,
		vendor,
		from,
		to,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	} = props;

	const isValidDatesForGranularities = dateSelectionMode === EDateSelectionModes.static
		? (!!from && !!to)
		: dynamicDateRange?.value === EDynamicDateRange.custom
			? startDateOffset != null
			: !!dynamicDateRange;

	const dateSelectionParameters = getDateSelectionParameters({
		isOranVendor,
		values: {
			dateSelectionMode,
			dynamicDateRange,
			startDateOffset,
		},
		dateFromUTC: from,
		dateToUTC: to,
	});

	const {
		data: automaticGranularity,
		isInitialLoading,
		isFetching,
	} = useQuery<TNameTitle<EGranularityValue>>({
		queryKey: [OPTIONS_GRANULARITIES_AUTOMATIC, {
			vendor,
			...dateSelectionParameters,
		}],
		queryFn: extractQueryParams(getAutomaticGranularity),
		allowedRoles: [EUserRoles.networkOperatorKpiUpdater, EUserRoles.networkOperatorKpiViewer],
		enabled: [
			isOranVendor,
			vendor,
			isValidDatesForGranularities,
		],
		...refetchOptions,
	});

	return {
		automaticGranularity: automaticGranularity,
		isLoadingOptionsAutomaticGranularity: isFetching || isInitialLoading,
	};
};

